import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../services/api';

const DriverNotificationContext = createContext(null);

export const useDriverNotifications = () => {
  const context = useContext(DriverNotificationContext);
  if (!context) {
    throw new Error('useDriverNotifications must be used within DriverNotificationProvider');
  }
  return context;
};

export const DriverNotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [loading, setLoading] = useState(false);

  // Check if push is supported
  const isPushSupported = () => {
    return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
  };

  // Register service worker
  const registerServiceWorker = async () => {
    if (!('serviceWorker' in navigator)) {
      console.log('Service workers not supported');
      return null;
    }

    try {
      const registration = await navigator.serviceWorker.register('/sw.js');
      console.log('Service Worker registered:', registration);
      return registration;
    } catch (error) {
      console.error('Service Worker registration failed:', error);
      return null;
    }
  };

  // Get VAPID public key from server
  const getVapidKey = async () => {
    try {
      const response = await api.get('/driver/vapid-public-key');
      return response.data.publicKey;
    } catch (error) {
      console.error('Failed to get VAPID key:', error);
      return null;
    }
  };

  // Convert VAPID key to Uint8Array
  const urlBase64ToUint8Array = (base64String) => {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  };

  // Subscribe to push notifications
  const subscribeToPush = async () => {
    if (!isPushSupported()) {
      console.log('Push notifications not supported');
      return false;
    }

    try {
      // Request notification permission
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        console.log('Notification permission denied');
        return false;
      }

      // Register service worker
      const registration = await registerServiceWorker();
      if (!registration) return false;

      // Get VAPID key
      const vapidKey = await getVapidKey();
      if (!vapidKey) return false;

      // Subscribe to push
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey)
      });

      // Send subscription to server
      const subJson = subscription.toJSON();
      await api.post('/driver/push/subscribe', {
        endpoint: subJson.endpoint,
        keys: subJson.keys
      });

      setPushEnabled(true);
      localStorage.setItem('driver_push_enabled', 'true');
      console.log('Push subscription successful');
      return true;
    } catch (error) {
      console.error('Failed to subscribe to push:', error);
      return false;
    }
  };

  // Unsubscribe from push notifications
  const unsubscribeFromPush = async () => {
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      
      if (subscription) {
        await subscription.unsubscribe();
        await api.delete('/driver/push/unsubscribe', {
          params: { endpoint: subscription.endpoint }
        });
      }
      
      setPushEnabled(false);
      localStorage.removeItem('driver_push_enabled');
      console.log('Push unsubscription successful');
      return true;
    } catch (error) {
      console.error('Failed to unsubscribe from push:', error);
      return false;
    }
  };

  // Fetch notifications from server
  const fetchNotifications = useCallback(async () => {
    const token = localStorage.getItem('driver_token');
    if (!token) return;

    setLoading(true);
    try {
      const response = await api.get('/driver/notifications');
      setNotifications(response.data.notifications || []);
      setUnreadCount(response.data.unread_count || 0);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Mark notification as read
  const markAsRead = async (notificationId) => {
    try {
      await api.put(`/driver/notifications/${notificationId}/read`);
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  // Mark all as read
  const markAllAsRead = async () => {
    try {
      await api.put('/driver/notifications/read-all');
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };

  // Check push status on mount
  useEffect(() => {
    const checkPushStatus = async () => {
      if (!isPushSupported()) return;

      try {
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();
        setPushEnabled(!!subscription);
      } catch (error) {
        console.error('Failed to check push status:', error);
      }
    };

    checkPushStatus();
  }, []);

  // Fetch notifications when token exists
  useEffect(() => {
    const token = localStorage.getItem('driver_token');
    if (token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchNotifications();
    }
  }, [fetchNotifications]);

  const value = {
    notifications,
    unreadCount,
    pushEnabled,
    loading,
    isPushSupported: isPushSupported(),
    subscribeToPush,
    unsubscribeFromPush,
    fetchNotifications,
    markAsRead,
    markAllAsRead
  };

  return (
    <DriverNotificationContext.Provider value={value}>
      {children}
    </DriverNotificationContext.Provider>
  );
};

export default DriverNotificationProvider;
